import React, { useState, useEffect } from "react";
import { dataService } from "../../../../../Services/data/data.service";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
// import admin from "firebase-admin";
import { Factory, HandCoins, Repeat, X, Plus, Save, Trash2, History, AlertTriangle, Check, Pencil } from "lucide-react";
import { crudTypes } from "../../../../../Config/constants";
import { processPriceInput, parsePrice } from "../../../../../Utils/priceInput";
// components
import CustomCheckbox from "../../../../Shared/Checkbox";
import Button from "../../../../Shared/Buttons/Button";
import Textarea from "../../../../Shared/Textarea";
import Chip from "../../../../Shared/ui/Chip";
import Input from "../../../../Shared/Input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectGroup, SelectLabel } from "../../../../Shared/Select";
import Divider from "../../../../Shared/ui/Divider";

export default function PriceDefinitionDialog({ dialogOpen, closeDialog, priceDefinition, crudType, ...props }) {
  const classes = useStyles();

  const [generalError, setGeneralError] = useState(null);
  const [disclaimer, setDisclaimer] = useState(null);

  const [schedulingPriceChange, setSchedulingPriceChange] = useState(false);

  // Data
  const [allCompanies, setAllCompanies] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedCompanyError, setSelectedCompanyError] = useState(null);

  const [selectedAccount, setSelectedAccount] = useState(null);

  const [code, setCode] = useState(null);
  const [codeError, setCodeError] = useState(null);

  const [name, setName] = useState(null);
  const [nameError, setNameError] = useState(null);

  const [description, setDescription] = useState(null);
  const [descriptionError, setDescriptionError] = useState(null);

  const [price, setPrice] = useState(null);
  const [priceError, setPriceError] = useState(null);

  const [uom, setUom] = useState(null);
  const [uomError, setUomError] = useState(null);

  const [recurrencePeriod, setRecurrencePeriod] = useState(null);
  const [recurrencePeriodError, setRecurrencePeriodError] = useState(null);

  const [allPriceEntries, setAllPriceEntries] = useState([]);

  const [createAnotherDefinition, setCreateAnotherDefinition] = useState(false);

  const [scheduledPrice, setScheduledPrice] = useState("");
  const [scheduledPriceError, setScheduledPriceError] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState(new Date());

  // Confirmation state for deleting price entries
  const [confirmDelete, setConfirmDelete] = useState(null); // stores the priceChangeId to delete

  // Confirmation state for deleting the entire price definition
  const [confirmDeleteDefinition, setConfirmDeleteDefinition] = useState(false);

  // History view state
  const [showingHistory, setShowingHistory] = useState(false);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Correction panel state
  const [correctionPanelOpen, setCorrectionPanelOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [correctionStep, setCorrectionStep] = useState(1);
  const [correctedPrice, setCorrectedPrice] = useState("");
  const [correctedPriceError, setCorrectedPriceError] = useState(null);
  const [correctionReason, setCorrectionReason] = useState("");
  const [correctionReasonError, setCorrectionReasonError] = useState(null);
  const [correctionResult, setCorrectionResult] = useState(null);
  const [correctionSubmitting, setCorrectionSubmitting] = useState(false);

  useEffect(() => {
    if (props.Login && props.Login.userid) {
      // Get all companies
      dataService
        .getAllCompaniesByUserId(props.Login.userid)
        .then((response) => {
          setAllCompanies(response);
          if (response.length === 1) {
            setSelectedCompany(response[0]);
          }
        })
        .catch((error) => {
          console.error("Error fetching companies:", error);
          setGeneralError("An error occurred while fetching companies. Please try again later.");
        });

      // Get all accountCategories & accounts (used for the DefaultAccount selector)
      dataService
        .getAllUserCompanyAccounts(props.Login.userid, 0, 0)
        .then((response) => {
          setAllAccounts(response || []);
        })
        .catch((error) => {
          console.error("Error fetching accounts:", error);
        });
    }
  }, [props.Login.userid]);

  useEffect(() => {
    resetErrors();
    resetFields();
    if (crudType === crudTypes.CREATE) {
      console.log("Creating new price definition");
    } else if (crudType === crudTypes.UPDATE) {
      if (priceDefinition) {
        setSelectedCompany(allCompanies.find((company) => company.id === priceDefinition.companyId));
        setCode(priceDefinition.code);
        setName(priceDefinition.name);
        setDescription(priceDefinition.description);
        const currentPrice = priceDefinition.entries.find((entry) => entry.isCurrentPrice)?.price;
        if (currentPrice) {
          // Format price with 3 decimals and European formatting for display in input
          const formattedPrice = currentPrice.toFixed(3).replace(/\B(?=(\d{3})+(?!\d))/g, ".").replace(/\.(\d{3})$/, ",$1");
          setPrice(formattedPrice);
        } else {
          setPrice(null);
        }
        setUom(priceDefinition.entries.find((entry) => entry.isCurrentPrice)?.unitOfMeasurement || null);
        setSelectedAccount(findAccountById(priceDefinition.defaultAccountId, priceDefinition.companyId));
      }
    } else if (crudType === crudTypes.READ) {
      if (priceDefinition) {
        console.log("Reading price definition", priceDefinition);
        setSelectedCompany(allCompanies.find((company) => company.id === priceDefinition.companyId));
        setCode(priceDefinition.code);
        setName(priceDefinition.name);
        setDescription(priceDefinition.description);
        const currentPrice = priceDefinition.entries.find((entry) => entry.isCurrentPrice)?.price;
        if (currentPrice) {
          // Format price with 3 decimals and European formatting for display in input
          const formattedPrice = currentPrice.toFixed(3).replace(/\B(?=(\d{3})+(?!\d))/g, ".").replace(/\.(\d{3})$/, ",$1");
          setPrice(formattedPrice);
        } else {
          setPrice(null);
        }
        setUom(priceDefinition.entries.find((entry) => entry.isCurrentPrice)?.unitOfMeasurement || null);
        setSelectedAccount(findAccountById(priceDefinition.defaultAccountId, priceDefinition.companyId));
        setAllPriceEntries(priceDefinition.entries || []);
      }
    } else {
      setDisclaimer("What are you gonna do?");
    }
  }, [crudType, priceDefinition, allCompanies, allAccounts]);

  const findAccountById = (accountId, companyId) => {
    if (!accountId || !companyId) return null;
    const company = allAccounts.find((c) => c.id === companyId);
    if (!company) return null;
    for (const category of company.accountCategories || []) {
      const found = (category.accounts || []).find((a) => a.id === accountId);
      if (found) return found;
    }
    return null;
  };

  const getDialogTitle = () => {
    switch (crudType) {
      case crudTypes.CREATE:
        return "Create Price Definition";
      case crudTypes.UPDATE:
        return "Edit" + (priceDefinition ? ` ${priceDefinition.name}` : "");
      case crudTypes.READ:
        return priceDefinition ? `${priceDefinition.code} - ${priceDefinition.name}` : "Price Definition";
      default:
        return "Subscriber";
    }
  };

  const getSubmitLabel = () => {
    switch (crudType) {
      case crudTypes.CREATE:
        return "Create Price Definition";
      case crudTypes.UPDATE:
        return "Save Changes";
      default:
        return "Submit";
    }
  };

  const changeSelectedCompany = (company) => {
    setSelectedCompany(company);
  };

  const changeCode = (e) => {
    setCode(e.target.value);
    if (e.target.value.length < 1) {
      setCode(null);
    }
  };

  const changeName = (e) => {
    setName(e.target.value);
    if (e.target.value.length < 1) {
      setName(null);
    }
  };

  const changeDescription = (e) => {
    setDescription(e.target.value);
    if (e.target.value.length < 1) {
      setDescription(null);
    }
  };

  const changePrice = (e) => {
    setPrice(e.target.value);
    if (e.target.value.length < 1) {
      setPrice(null);
    }
  };

  const changeUom = (e) => {
    setUom(e.target.value);
    if (e.target.value.length < 1) {
      setUom(null);
    }
  };

  const resetErrors = () => {
    setNameError(null);
    setCodeError(null);
    setDescriptionError(null);
    setPriceError(null);
    setUomError(null);
    setRecurrencePeriodError(null);
    setSelectedCompanyError(null);
    setGeneralError(null);
  };

  const resetFields = () => {
    setName("");
    setSelectedCompany(null);
    setSelectedAccount(null);
    setCode("");
    setDescription("");
    setPrice(null);
    setUom("");
    setRecurrencePeriod(null);
    setCreateAnotherDefinition(false);
    setDisclaimer(null);
  };

  const resetFieldsKeepCompany = () => {
    setName("");
    setCode("");
    setDescription("");
    setPrice(null);
    setUom("");
    setRecurrencePeriod(null);
    setSelectedAccount(null);
    setDisclaimer(null);
    // Keep selectedCompany and createAnotherDefinition unchanged
  };

  const handleCloseDialog = () => {
    setSchedulingPriceChange(false);
    setConfirmDeleteDefinition(false);
    setShowingHistory(false);
    setHistoryEntries([]);
    setCorrectionPanelOpen(false);
    setSelectedPeriod(null);
    setCorrectionStep(1);
    setCorrectedPrice("");
    setCorrectedPriceError(null);
    setCorrectionReason("");
    setCorrectionReasonError(null);
    setCorrectionResult(null);
    resetErrors();
    resetFields();
    closeDialog();
  };

  const handleToggleHistory = () => {
    if (showingHistory) {
      setShowingHistory(false);
      setCorrectionPanelOpen(false);
      setSelectedPeriod(null);
      setCorrectionStep(1);
      return;
    }
    if (!priceDefinition) return;
    setHistoryLoading(true);
    dataService
      .getPriceHistory(priceDefinition.id)
      .then((entries) => {
        setHistoryEntries(entries || []);
        setShowingHistory(true);
      })
      .catch(() => {
        setGeneralError("An error occurred while fetching price history.");
      })
      .finally(() => {
        setHistoryLoading(false);
      });
  };

  const handleSelectPeriod = (entry) => {
    if (entry.isCurrentPrice || new Date(entry.validFrom) > new Date()) return;
    if (selectedPeriod && selectedPeriod.id === entry.id && correctionPanelOpen) return;
    const formatPriceForInput = (val) => {
      return val.toFixed(3).replace(/\B(?=(\d{3})+(?!\d))/g, ".").replace(/\.(\d{3})$/, ",$1");
    };
    setSelectedPeriod(entry);
    setCorrectedPrice(formatPriceForInput(entry.price));
    setCorrectedPriceError(null);
    setCorrectionReason("");
    setCorrectionReasonError(null);
    setCorrectionStep(1);
    setCorrectionResult(null);
    setCorrectionPanelOpen(true);
  };

  const handleCloseCorrectionPanel = () => {
    setCorrectionPanelOpen(false);
    setSelectedPeriod(null);
    setCorrectionStep(1);
    setCorrectedPrice("");
    setCorrectedPriceError(null);
    setCorrectionReason("");
    setCorrectionReasonError(null);
    setCorrectionResult(null);
  };

  const handleCorrectionNext = () => {
    let valid = true;
    setCorrectedPriceError(null);
    setCorrectionReasonError(null);
    const parsed = parsePrice(correctedPrice);
    if (!correctedPrice || parsed === null) {
      setCorrectedPriceError("A valid price is required");
      valid = false;
    }
    if (!correctionReason || correctionReason.trim().length < 1) {
      setCorrectionReasonError("A reason is required");
      valid = false;
    }
    if (valid) {
      setCorrectionStep(2);
    }
  };

  const handleCorrectionSubmit = async () => {
    setCorrectionSubmitting(true);
    const parsedPrice = parsePrice(correctedPrice);
    const payload = {
      priceEntryId: selectedPeriod.id,
      priceDefinitionId: priceDefinition.id,
      originalPrice: selectedPeriod.price,
      correctedPrice: parsedPrice,
      reason: correctionReason.trim(),
      correctedByUserId: props.Login.userid,
    };
    try {
      const response = await dataService.correctPriceEntry(payload);
      if (response.success) {
        const result = {
          periodId: selectedPeriod.id,
          originalPrice: selectedPeriod.price,
          correctedPrice: parsedPrice,
          reason: correctionReason.trim(),
          correctedAt: response.correctedAt || new Date().toISOString(),
          correctedBy: props.Login.displayName || props.Login.userid,
        };
        setCorrectionResult(result);
        setCorrectionStep(3);
        setHistoryEntries((prev) =>
          prev.map((e) =>
            e.id === selectedPeriod.id ? { ...e, price: parsedPrice, corrected: true } : e
          )
        );
        if (props.onCorrectionApplied) {
          props.onCorrectionApplied(result);
        }
      } else {
        setGeneralError(response.message || "An error occurred while applying the correction.");
        setCorrectionStep(1);
      }
    } catch {
      setGeneralError("An error occurred while applying the correction.");
      setCorrectionStep(1);
    } finally {
      setCorrectionSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    switch (crudType) {
      case crudTypes.CREATE:
        handleCreatePriceDefinition();
        break;
      case crudTypes.UPDATE:
        handleUpdatePriceDefinition();
        break;
      default:
        setGeneralError("An error occurred while processing your request. Please try again later.");
        return;
    }
  };

  const handleCreatePriceDefinition = async () => {
    const isValid = validateCreateFields();

    if (!isValid) {
      return;
    }
    // dataservice
    const newPriceDefinition = {
      code: code,
      name: name,
      description: description,
      companyId: selectedCompany.id,
      currencyId: selectedCompany.currencyId,
      price: parsePrice(price),
      unitOfMeasurement: uom,
      defaultAccountId: selectedAccount?.id ?? null,
      insertedByUserId: props.Login.userid,
    };

    dataService
      .createPriceDefinition(newPriceDefinition)
      .then((response) => {
        if (response.success) {
          toast.success("Price definition created successfully.");
          if (props.onAdd) {
            props.onAdd(response.priceDefinition);
          }
          if (createAnotherDefinition) {
            resetErrors();
            resetFieldsKeepCompany();
          } else {
            handleCloseDialog();
          }
        } else {
          setGeneralError(response.message || "An error occurred while creating the price definition. Please try again later.");
          return;
        }
      })
      .catch((error) => {
        setGeneralError("An error occurred while creating the price definition. Please try again later.");
      });
  };

  const handleUpdatePriceDefinition = async () => {
    const isValid = validateUpdateFields();
    if (!isValid) {
      return;
    }
    // dataservice
    const updatedPriceDefinition = {
      id: priceDefinition.id,
      code: code,
      name: name,
      description: description,
      currencyId: selectedCompany.currencyId,
      price: parsePrice(price),
      unitOfMeasurement: uom,
      defaultAccountId: selectedAccount?.id ?? null,
      updatedByUserId: props.Login.userid,
    };
    dataService
      .updatePriceDefinition(updatedPriceDefinition)
      .then((response) => {
        if (response.success) {
          toast.success("Price definition updated successfully.");
          if (props.onUpdate) {
            props.onUpdate(response.priceDefinition);
          }
          handleCloseDialog();
        } else {
          setGeneralError(response.message || "An error occurred while updating the price definition. Please try again later.");
          return;
        }
      })
      .catch((error) => {
        setGeneralError("An error occurred while updating the price definition. Please try again later.");
      });
  };

  const handleSchedulePriceChange = () => {
    setGeneralError(null);
    // Convert datetime-local format to Taplr format (day/month/year)
    const formatDateForBackend = (datetimeLocalValue) => {
      const date = new Date(datetimeLocalValue);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    };

    const newPriceChange = {
      priceDefinitionId: priceDefinition.id,
      price: parsePrice(scheduledPrice),
      validFrom: formatDateForBackend(effectiveDate),
      insertedByUserId: props.Login.userid,
    };
    dataService
      .schedulePriceChange(newPriceChange)
      .then((response) => {
        if (response.success) {
          // Sort entries: current price first, then by ValidFrom date (earliest first)
          const sortedEntries = (response.priceDefinition.entries || []).sort((a, b) => {
            // Current price always first
            if (a.isCurrentPrice && !b.isCurrentPrice) return -1;
            if (!a.isCurrentPrice && b.isCurrentPrice) return 1;
            
            // If both are current or both are not current, sort by validFrom
            const dateA = new Date(a.validFrom || 0);
            const dateB = new Date(b.validFrom || 0);
            return dateA - dateB;
          });
          
          setAllPriceEntries(sortedEntries);
          if (props.onUpdate) {
            props.onUpdate(response.priceDefinition);
          }
          setSchedulingPriceChange(false);
          setScheduledPrice("");
          setEffectiveDate(new Date());
        } else {
          setGeneralError(response.message || "An error occurred while scheduling the price change. Please try again later.");
          return;
        }
      })
      .catch((error) => {
        setGeneralError("An error occurred while scheduling the price change. Please try again later.");
      })
      .finally(() => {});
  };

  const handleDeletePriceChange = (priceChangeId) => {
    dataService
      .deletePriceChange(priceChangeId)
      .then((response) => {
        if (response.success) {
          toast.success("Price change deleted successfully.");
          setAllPriceEntries((prevEntries) => prevEntries.filter((entry) => entry.id !== priceChangeId));
          props.onUpdate(response.priceDefinition);
        } else {
          setGeneralError(response.message || "An error occurred while deleting the price change. Please try again later.");
          return;
        }
      })
      .catch((error) => {
        setGeneralError("An error occurred while deleting the price change. Please try again later.");
      })
      .finally(() => {
        setConfirmDelete(null); // Clear confirmation state
      });
  };

  const handleConfirmDelete = (priceChangeId) => {
    setConfirmDelete(priceChangeId);
  };

  const handleCancelDelete = () => {
    setConfirmDelete(null);
  };

  const handleDeletePriceDefinition = () => {
    dataService
      .deletePriceDefinition(priceDefinition.id)
      .then((response) => {
        if (response.success) {
          toast.success("Price definition deleted successfully.");
          if (props.onDelete) {
            props.onDelete(priceDefinition.id);
          }
          setConfirmDeleteDefinition(false);
          handleCloseDialog();
        } else {
          setGeneralError(response.message || "An error occurred while deleting the price definition. Please try again later.");
          setConfirmDeleteDefinition(false);
        }
      })
      .catch((error) => {
        setGeneralError("An error occurred while deleting the price definition. Please try again later.");
        setConfirmDeleteDefinition(false);
      });
  };

  const validateCreateFields = () => {
    let isValid = true;
    resetErrors();
    if (!selectedCompany) {
      setSelectedCompanyError("Required");
      isValid = false;
    }
    if (!code || code.length < 1) {
      setCodeError("Required");
      isValid = false;
    }
    if (!name || name.length < 1) {
      setNameError("Required");
      isValid = false;
    }
    if (!price || price.length < 1) {
      setPriceError("Required");
      isValid = false;
    }
    if (!uom || uom.length < 1) {
      setUomError("Required");
      isValid = false;
    }
    return isValid;
  };

  const validateUpdateFields = () => {
    let isValid = true;
    resetErrors();
    if (!code || code.length < 1) {
      setCodeError("Required");
      isValid = false;
    }
    if (!name || name.length < 1) {
      setNameError("Required");
      isValid = false;
    }
    if (!price || price.length < 1) {
      setPriceError("Required");
      isValid = false;
    }
    if (!uom || uom.length < 1) {
      setUomError("Required");
      isValid = false;
    }
    return isValid;
  };

  // Price Entry component
  const PriceEntry = ({ entry, previousEntry }) => {
    const calculatePercentageChange = () => {
      if (!previousEntry || previousEntry.price === 0) return null;
      const change = ((entry.price - previousEntry.price) / previousEntry.price) * 100;
      return change;
    };

    const percentageChange = calculatePercentageChange();

    const formatPrice = (priceEntry) => {
      if (priceEntry) {
        return `${priceEntry.currencySymbol}${priceEntry.price
          .toFixed(3)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ".")
          .replace(/\.(\d{3})$/, ",$1")}`;
      } else {
        return "N/A";
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return (
        date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }) +
        " at " +
        date.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      );
    };

    return (
      <div>
        {!entry.isCurrentPrice && entry.validFrom && <p className={classes.validFromDate}>{formatDate(entry.validFrom)}</p>}
        <div className={classes.priceEntry}>
          <div className={classes.priceInfo}>
            <p className={classes.price}>
              {formatPrice(entry) + "/" + entry.unitOfMeasurement}
              {!entry.isCurrentPrice && percentageChange !== null && (
                <Chip variant={percentageChange >= 0 ? "success" : "danger"} size="small" style={{ marginLeft: "8px" }}>
                  {percentageChange >= 0 ? "+" : ""}
                  {percentageChange.toFixed(1)}%
                </Chip>
              )}
            </p>
          </div>
          {entry.isCurrentPrice && <Chip variant="success">Active Price</Chip>}
          {!entry.isCurrentPrice && (
            <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
              {confirmDelete === entry.id ? (
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <div className={classes.slideInFromRight}>
                    <Button variant="destructive" size="small" onClick={() => handleDeletePriceChange(entry.id)}>
                      Confirm Delete
                    </Button>
                  </div>
                  <div className={classes.fadeIn}>
                    <Button iconButton variant="secondary" size="small" onClick={handleCancelDelete} startIcon={<X />} />
                  </div>
                </div>
              ) : (
                <div className={confirmDelete ? classes.slideOutToRight : ""}>
                  <Button iconButton variant="secondary" size="small" onClick={() => handleConfirmDelete(entry.id)} startIcon={<Trash2 />} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
    <Dialog className={classes.dialog} open={dialogOpen} aria-labelledby="draggable-dialog-title" maxWidth={false} classes={{ paper: correctionPanelOpen ? classes.dialogPaperExpanded : classes.dialogPaper }}>
      <div className={classes.dialog__head}>
        <DialogTitle className={classes.dialog__title} id="draggable-dialog-title">
          {showingHistory ? "Price History" : getDialogTitle()}
        </DialogTitle>
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          {crudType === crudTypes.READ && priceDefinition && (
            <button onClick={handleToggleHistory} className={`${classes.dialog__historyButton} ${showingHistory ? classes.dialog__historyButtonActive : ""}`} title="Price history">
              <History size={17} />
            </button>
          )}
          <button onClick={() => handleCloseDialog()} className={classes.dialog__closeButton}>
            <X size={19} />
          </button>
        </div>
      </div>
      <DialogContent className={classes.dialog__contentContainer}>
        <div className={classes.dialog__content}>
          {generalError && (
            <div className={classes.errorContainer}>
              <p className={classes.errorMessage}>{generalError}</p>
            </div>
          )}
          {(crudType === crudTypes.UPDATE || (crudType === crudTypes.READ && !showingHistory)) && (
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: "8px", width: "100%", marginBottom: "16px" }}>
              {selectedCompany && (
                <Chip variant="light" startAdornment={<Factory />}>
                  {selectedCompany.name}
                </Chip>
              )}
              {selectedAccount && (
                <Chip variant="light" startAdornment={<HandCoins />}>
                  {selectedAccount.name}
                </Chip>
              )}
            </div>
          )}
          {/* --- History View --- */}
          {crudType === crudTypes.READ && showingHistory && (
            <div className={correctionPanelOpen ? classes.historyWithCorrectionLayout : undefined} style={{ width: "100%" }}>
              <div className={classes.priceEntries} style={correctionPanelOpen ? { width: "auto", flex: "0 0 380px" } : undefined}>
                {historyLoading ? (
                  <p style={{ textAlign: "center", color: "#666", fontSize: "14px" }}>Loading history...</p>
                ) : historyEntries.length === 0 ? (
                  <p style={{ textAlign: "center", color: "#666", fontSize: "14px" }}>No price history available.</p>
                ) : (
                  historyEntries.map((entry, index) => {
                    const previousEntry = index > 0 ? historyEntries[index - 1] : null;
                    const percentageChange = previousEntry && previousEntry.price !== 0
                      ? ((entry.price - previousEntry.price) / previousEntry.price) * 100
                      : null;
                    const isPastPeriod = !entry.isCurrentPrice && entry.validTo && new Date(entry.validTo) < new Date();

                    const formatHistoryPrice = (e) => {
                      return `${e.currencySymbol}${e.price.toFixed(3).replace(/\B(?=(\d{3})+(?!\d))/g, ".").replace(/\.(\d{3})$/, ",$1")}`;
                    };

                    const formatHistoryDate = (dateString) => {
                      if (!dateString) return "";
                      const date = new Date(dateString);
                      return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) +
                        " at " +
                        date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
                    };

                    return (
                      <div key={entry.id}>
                        <p className={classes.validFromDate}>{formatHistoryDate(entry.validFrom)}</p>
                        <div
                          className={`${classes.priceEntry} ${selectedPeriod?.id === entry.id ? classes.priceEntrySelected : ""}`}
                        >
                          <div className={classes.priceInfo}>
                            <p className={classes.price}>
                              {formatHistoryPrice(entry) + "/" + entry.unitOfMeasurement}
                              {percentageChange !== null && (
                                <Chip variant={percentageChange >= 0 ? "success" : "danger"} size="small" style={{ marginLeft: "8px" }}>
                                  {percentageChange >= 0 ? "+" : ""}{percentageChange.toFixed(1)}%
                                </Chip>
                              )}
                            </p>
                          </div>
                          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                            {entry.corrected && <Chip variant="info" size="small">Corrected</Chip>}
                            {entry.isCurrentPrice && <Chip variant="success">Active</Chip>}
                            {isPastPeriod && !entry.corrected && <Chip variant="light" size="small">Expired</Chip>}
                            {!entry.isCurrentPrice && new Date(entry.validFrom) > new Date() && (
                              <Chip variant="warning" size="small">Scheduled</Chip>
                            )}
                            {isPastPeriod && (
                              <Button
                                iconButton
                                variant="secondary"
                                size="small"
                                onClick={() => handleSelectPeriod(entry)}
                                startIcon={<Pencil size={14} />}
                                title="Correct this price"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              {correctionPanelOpen && selectedPeriod && (
                <div className={classes.correctionPanel}>
                  {/* Step 1: Edit */}
                  {correctionStep === 1 && (
                    <div className={classes.correctionPanelContent}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                        <p style={{ margin: 0, fontSize: "16px", fontWeight: "500", color: "#0C0E29" }}>Correct Price</p>
                        <button onClick={handleCloseCorrectionPanel} className={classes.dialog__closeButton}>
                          <X size={17} />
                        </button>
                      </div>
                      <div className={classes.inputContainer}>
                        <div className={classes.labelContainer}>
                          <label style={{ fontSize: "14px", color: "#0C0E29", opacity: 0.8 }}>Period</label>
                        </div>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <Input
                            type="text"
                            value={selectedPeriod.validFrom ? new Date(selectedPeriod.validFrom).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : ""}
                            readOnly
                          />
                          <Input
                            type="text"
                            value={selectedPeriod.validTo ? new Date(selectedPeriod.validTo).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : ""}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className={classes.inputContainer}>
                        <div className={classes.labelContainer}>
                          <label style={{ fontSize: "14px", color: "#0C0E29", opacity: 0.8 }}>Corrected Price*</label>
                          {correctedPriceError && <p className={classes.inputError}>{correctedPriceError}</p>}
                        </div>
                        <Input
                          placeholder="0,000"
                          type="text"
                          onChange={(event) => {
                            const formatted = processPriceInput(event.target.value);
                            if (formatted !== null) setCorrectedPrice(formatted);
                          }}
                          value={correctedPrice || ""}
                          error={correctedPriceError !== null}
                          style={{ backgroundColor: "white" }}
                        />
                      </div>
                      <div className={classes.inputContainer}>
                        <div className={classes.labelContainer}>
                          <label style={{ fontSize: "14px", color: "#0C0E29", opacity: 0.8 }}>Reason*</label>
                          {correctionReasonError && <p className={classes.inputError}>{correctionReasonError}</p>}
                        </div>
                        <Textarea
                          placeholder="Explain why this price is being corrected..."
                          value={correctionReason}
                          onChange={(e) => setCorrectionReason(e.target.value)}
                          error={correctionReasonError !== null}
                          rows={3}
                          style={{ backgroundColor: "white" }}
                        />
                      </div>
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "8px" }}>
                        <Button variant="secondary" size="small" onClick={handleCloseCorrectionPanel}>Cancel</Button>
                        <Button variant="primary" size="small" onClick={handleCorrectionNext}>Review</Button>
                      </div>
                    </div>
                  )}
                  {/* Step 2: Review */}
                  {correctionStep === 2 && (
                    <div className={classes.correctionPanelContent}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                        <p style={{ margin: 0, fontSize: "16px", fontWeight: "500", color: "#0C0E29" }}>Review Correction</p>
                        <button onClick={handleCloseCorrectionPanel} className={classes.dialog__closeButton}>
                          <X size={17} />
                        </button>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "14px", color: "#0C0E29" }}>
                          <span style={{ opacity: 0.8 }}>Original Price</span>
                          <span style={{ fontWeight: "500" }}>
                            {selectedPeriod.currencySymbol}{selectedPeriod.price.toFixed(3).replace(/\B(?=(\d{3})+(?!\d))/g, ".").replace(/\.(\d{3})$/, ",$1")}
                          </span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "14px", color: "#0C0E29" }}>
                          <span style={{ opacity: 0.8 }}>Corrected Price</span>
                          <span style={{ fontWeight: "500" }}>
                            {selectedPeriod.currencySymbol}{correctedPrice}
                          </span>
                        </div>
                        <Divider />
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "14px", color: "#0C0E29" }}>
                          <span style={{ opacity: 0.8 }}>Difference</span>
                          <span style={{ fontWeight: "500" }}>
                            {(() => {
                              const parsed = parsePrice(correctedPrice);
                              const diff = parsed - selectedPeriod.price;
                              const sign = diff >= 0 ? "+" : "";
                              return `${sign}${diff.toFixed(3).replace(/\B(?=(\d{3})+(?!\d))/g, ".").replace(/\.(\d{3})$/, ",$1")}`;
                            })()}
                          </span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "14px", color: "#0C0E29" }}>
                          <span style={{ opacity: 0.8 }}>Reason</span>
                          <span style={{ fontWeight: "500", maxWidth: "180px", textAlign: "right" }}>{correctionReason}</span>
                        </div>
                      </div>
                      <div style={{ marginTop: "16px", padding: "12px", borderRadius: "6px", backgroundColor: "#FDF3F3", border: "1px solid #FBE5E5" }}>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                          <AlertTriangle size={16} style={{ color: "#D84949", flexShrink: 0, marginTop: "2px" }} />
                          <p style={{ margin: 0, fontSize: "14px", color: "#D84949" }}>
                            This correction is irreversible. The original price will be preserved in the audit log but the corrected price will apply to this period.
                          </p>
                        </div>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", marginTop: "16px" }}>
                        <Button variant="secondary" size="small" onClick={() => setCorrectionStep(1)}>Back</Button>
                        <Button variant="destructive" size="small" loading={correctionSubmitting} onClick={handleCorrectionSubmit}>Confirm Correction</Button>
                      </div>
                    </div>
                  )}
                  {/* Step 3: Confirmed */}
                  {correctionStep === 3 && correctionResult && (
                    <div className={classes.correctionPanelContent}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                        <p style={{ margin: 0, fontSize: "16px", fontWeight: "500", color: "#0C0E29" }}>Correction Applied</p>
                        <button onClick={handleCloseCorrectionPanel} className={classes.dialog__closeButton}>
                          <X size={17} />
                        </button>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", padding: "16px 0" }}>
                        <div style={{ width: "40px", height: "40px", borderRadius: "50%", backgroundColor: "#E8F5E9", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Check size={20} style={{ color: "#2E7D32" }} />
                        </div>
                        <p style={{ margin: 0, fontSize: "14px", color: "#0C0E29", textAlign: "center" }}>
                          The price for this period has been corrected and an audit log entry has been created.
                        </p>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px", padding: "12px", borderRadius: "8px", backgroundColor: "#F8F8F8", border: "1px solid #EDEDED", fontSize: "14px", color: "#0C0E29" }}>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ opacity: 0.8 }}>Original</span>
                          <span>{selectedPeriod.currencySymbol}{correctionResult.originalPrice.toFixed(3).replace(/\B(?=(\d{3})+(?!\d))/g, ".").replace(/\.(\d{3})$/, ",$1")}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ opacity: 0.8 }}>Corrected</span>
                          <span>{selectedPeriod.currencySymbol}{correctionResult.correctedPrice.toFixed(3).replace(/\B(?=(\d{3})+(?!\d))/g, ".").replace(/\.(\d{3})$/, ",$1")}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ opacity: 0.8 }}>Corrected by</span>
                          <span>{correctionResult.correctedBy}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ opacity: 0.8 }}>Date</span>
                          <span>{new Date(correctionResult.correctedAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                        </div>
                      </div>
                      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "16px" }}>
                        <Button variant="primary" size="small" onClick={handleCloseCorrectionPanel}>Done</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {/* --- Read --- */}
          {crudType === crudTypes.READ && !showingHistory && priceDefinition && (
            <>
              {priceDefinition.description && <p className={classes.priceDefinitionDescription}>{priceDefinition.description}</p>}
              <div className={classes.priceEntries}>
                {allPriceEntries.map((entry, index) => (
                  <PriceEntry entry={entry} previousEntry={index > 0 ? allPriceEntries[index - 1] : null} index={index} key={index} />
                ))}
              </div>
              {/* --- Schedule price change --- */}
              {schedulingPriceChange && (
                <>
                  <Divider style={{ margin: "1rem 0" }}>Price change</Divider>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "12px",
                      padding: "8px",
                      border: "1px solid #EDEDED",
                      borderRadius: "8px",
                      background: "#F8F8F8",
                      width: "100%",
                    }}
                  >
                    <div style={{ display: "flex", gap: "8px" }}>
                      {/* <Input type="text" value={scheduledPrice} onChange={(e) => setScheduledPrice(e.target.value)} placeholder="0.00" /> */}
                      {/* <TextField
                        className={classes.textField}
                        placeholder="0,00"
                        type="text"
                        variant="outlined"
                        size="small"
                        onChange={(event) => {
                          let value = event.target.value.replace(/\./g, "");
                          // Allow only numbers and up to 3 decimals (with comma as decimal separator)
                          if (/^\d*,?\d{0,3}$/.test(value)) {
                            // Format with thousand separators (dots) and comma for decimals
                            const [intPart, decPart] = value.split(",");
                            const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
                            const formattedValue = decPart !== undefined ? `${formattedInt},${decPart}` : formattedInt;
                            setScheduledPrice(formattedValue);
                          }
                        }}
                        value={scheduledPrice || ""}
                        fullWidth={true}
                        error={scheduledPriceError}
                        style={{ textAlign: "right" }}
                      /> */}
                      <Input
                        placeholder="0,000"
                        type="text"
                        onChange={(event) => {
                          const formatted = processPriceInput(event.target.value);
                          if (formatted !== null) setScheduledPrice(formatted);
                        }}
                        value={scheduledPrice || ""}
                        fullWidth={true}
                        error={scheduledPriceError}
                        style={{ textAlign: "right", backgroundColor: "white" }}
                      />
                      <Input style={{ backgroundColor: "white" }} type="datetime-local" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
                    </div>
                    {/* <Button iconButton variant="secondary" size="small" onClick={handleSchedulePriceChange} startIcon={<Save />} /> */}
                    <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Button variant="secondary" size="small" onClick={() => setSchedulingPriceChange(false)}>
                        Cancel
                      </Button>
                      <Button variant="primary" size="small" startIcon={<Save />} onClick={handleSchedulePriceChange}>
                        Schedule
                      </Button>
                    </div>
                  </div>
                </>
              )}
              {!schedulingPriceChange && (
                <div style={{ width: "100%", display: "flex", justifyContent: "center", marginTop: "16px" }}>
                  <Button variant="ghost" size="small" startIcon={<Plus />} onClick={() => setSchedulingPriceChange(true)}>
                    Schedule price change
                  </Button>
                </div>
              )}
            </>
          )}
          {/* --- Create/Update --- */}
          {(crudType === crudTypes.CREATE || crudType === crudTypes.UPDATE) && (
            <div className={classes.form}>
              {crudType === crudTypes.CREATE && (
                <>
                  <div className={classes.inputContainer}>
                    <div className={classes.labelContainer}>
                      <label className={classes.labelText}>Company*</label>
                      <p className={classes.inputError}>{selectedCompanyError}</p>
                    </div>
                    <Select value={selectedCompany?.id} onChange={changeSelectedCompany} error={selectedCompanyError !== null}>
                      <SelectTrigger>{selectedCompany?.name || "Select a company"}</SelectTrigger>
                      <SelectContent>
                        {allCompanies.map((company) => (
                          <SelectItem key={company.id} value={company}>
                            {company.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <div style={{ display: "flex", flexDirection: "row", gap: "8px", width: "100%" }}>
                <div className={classes.inputContainer} style={{ flex: "1" }}>
                  <div className={classes.labelContainer}>
                    <label className={classes.labelText}>Code*</label>
                    <p className={classes.inputError}>{codeError}</p>
                  </div>
                  <Input type="text" onChange={(event) => changeCode(event)} value={code} fullWidth={true} error={codeError !== null} inputProps={{ maxLength: 50 }} />
                </div>
                <div className={classes.inputContainer} style={{ flex: "2" }}>
                  <div className={classes.labelContainer}>
                    <label className={classes.labelText}>Name*</label>
                    <p className={classes.inputError}>{nameError}</p>
                  </div>
                  <Input type="text" onChange={(event) => changeName(event)} value={name} fullWidth={true} error={nameError !== null} inputProps={{ maxLength: 50 }} />
                </div>
              </div>
              <div className={classes.inputContainer}>
                <div className={classes.labelContainer}>
                  <label className={classes.labelText}>Description</label>
                  <p className={classes.inputError}>{descriptionError}</p>
                </div>
                <Textarea value={description} onChange={(event) => changeDescription(event)} />
              </div>
              <div style={{ display: "flex", flexDirection: "row", gap: "8px", width: "100%" }}>
                <div className={classes.inputContainer}>
                  <div className={classes.labelContainer}>
                    <label className={classes.labelText}>Price*</label>
                    <p className={classes.inputError}>{priceError}</p>
                  </div>
                  <Input
                    placeholder="0,000"
                    type="text"
                    onChange={(event) => {
                      const formatted = processPriceInput(event.target.value);
                      if (formatted !== null) setPrice(formatted);
                    }}
                    value={price || ""}
                    fullWidth={true}
                    error={priceError !== null}
                    style={{ textAlign: "right" }}
                  />
                </div>
                <div className={classes.inputContainer}>
                  <div className={classes.labelContainer}>
                    <label className={classes.labelText}>UOM*</label>
                    <p className={classes.inputError}>{uomError}</p>
                  </div>
                  <Input type="text" onChange={(event) => changeUom(event)} value={uom} fullWidth={true} error={uomError !== null} inputProps={{ maxLength: 50 }} />
                </div>
              </div>
              <div className={classes.inputContainer}>
                <div className={classes.labelContainer}>
                  <label className={classes.labelText}>Default account</label>
                </div>
                <Select searchable={true} placeholder="Select an account (optional)" value={selectedAccount?.id ?? ""} onChange={setSelectedAccount}>
                  <SelectTrigger>{selectedAccount?.name || "No default account"}</SelectTrigger>
                  <SelectContent>
                    <SelectItem value={null}>No default account</SelectItem>
                    {allAccounts
                      .find((company) => company.id === selectedCompany?.id)
                      ?.accountCategories.map((category) => (
                        <SelectGroup key={category.id}>
                          <SelectLabel>
                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              {category.name}
                              <div style={{ height: "6px", width: "6px", borderRadius: "3px", backgroundColor: category.isRevenue ? "#5CC958" : "#D84949" }} />
                            </div>
                          </SelectLabel>
                          {category.accounts.map((account) => (
                            <SelectItem key={account.id} value={account}>
                              {account.name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {disclaimer && (
            <div className={classes.disclaimerContainer}>
              <p className={classes.disclaimerMessage}>{disclaimer}</p>
            </div>
          )}
        </div>
      </DialogContent>
      {(crudType === crudTypes.CREATE || crudType === crudTypes.UPDATE) && (
        <DialogActions sx={{ display: "flex", justifyContent: "space-between", paddingLeft: "16px", paddingRight: "16px", paddingBottom: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {crudType === crudTypes.UPDATE && (
              <Button iconButton variant="secondary" size="small" onClick={() => setConfirmDeleteDefinition(true)} startIcon={<Trash2 />} />
            )}
            {crudType === crudTypes.CREATE && (
              <div className={classes.inputContainer} style={{ width: "fit-content" }}>
                <CustomCheckbox checked={createAnotherDefinition} onChange={setCreateAnotherDefinition} label="Create another" />
              </div>
            )}
          </div>
          <Button onClick={() => handleSubmit()}>{getSubmitLabel()}</Button>
        </DialogActions>
      )}
    </Dialog>

    {/* Confirm Delete Dialog */}
    <Dialog open={confirmDeleteDefinition} onClose={() => setConfirmDeleteDefinition(false)} classes={{ paper: classes.confirmDialogPaper }}>
      <div className={classes.dialog__head}>
        <DialogTitle className={classes.dialog__title}>Delete Price Definition</DialogTitle>
        <button onClick={() => setConfirmDeleteDefinition(false)} className={classes.dialog__closeButton}>
          <X size={19} />
        </button>
      </div>
      <DialogContent className={classes.dialog__contentContainer}>
        <p style={{ margin: 0, fontSize: "16px", color: "#0C0E29" }}>
          Are you sure you want to delete <strong>{priceDefinition?.name}</strong>? This action cannot be undone.
        </p>
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "flex-end", gap: "8px", paddingLeft: "16px", paddingRight: "16px", paddingBottom: "16px" }}>
        <Button variant="secondary" size="small" onClick={() => setConfirmDeleteDefinition(false)}>Cancel</Button>
        <Button variant="destructive" size="small" onClick={handleDeletePriceDefinition}>Delete</Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

const useStyles = makeStyles(() => ({
  "@keyframes slideInFromRight": {
    "0%": {
      opacity: 0,
      transform: "translateX(20px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateX(0)",
    },
  },
  "@keyframes slideOutToRight": {
    "0%": {
      opacity: 1,
      transform: "translateX(0)",
    },
    "100%": {
      opacity: 0,
      transform: "translateX(20px)",
    },
  },
  "@keyframes fadeIn": {
    "0%": {
      opacity: 0,
    },
    "100%": {
      opacity: 1,
    },
  },
  slideInFromRight: {
    animation: "$slideInFromRight 0.3s ease-out",
  },
  slideOutToRight: {
    animation: "$slideOutToRight 0.2s ease-in",
  },
  fadeIn: {
    animation: "$fadeIn 0.3s ease-out",
  },
  dialog: {
    margin: "auto",
  },

  dialogPaper: {
    width: "400px !important",
    height: "auto",
    maxHeight: "80vh !important",
    borderRadius: "6px !important",
    transition: "width 300ms ease !important",
  },

  dialogPaperExpanded: {
    width: "860px !important",
    height: "auto",
    maxHeight: "80vh !important",
    borderRadius: "6px !important",
    transition: "width 300ms ease !important",
  },

  confirmDialogPaper: {
    width: "360px !important",
    height: "auto",
    borderRadius: "6px !important",
  },

  dialog__head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",

    padding: "16px 16px 0px 16px !important",
    marginBottom: "8px !important",
  },

  dialog__title: {
    fontSize: "24px !important",
    fontFamily: "Lato, sans-serif !important",
    fontWeight: "normal",

    padding: "0px !important",
  },

  dialog__closeButton: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",

    padding: "3px !important",
    height: "25px !important",

    borderRadius: "3px",

    transition: "all 0.2s",

    "&:hover": {
      backgroundColor: "#D84949",
      color: "#F5F5F5",
    },

    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },
  },

  dialog__historyButton: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",

    padding: "3px !important",
    width: "25px",
    height: "25px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    borderRadius: "3px",

    transition: "all 0.2s",

    "&:hover": {
      backgroundColor: "#E0E0E0",
    },

    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },
  },

  dialog__historyButtonActive: {
    backgroundColor: "#4654FB",
    color: "#F5F5F5",

    "&:hover": {
      backgroundColor: "#3544d9",
      color: "#F5F5F5",
    },
  },

  dialog__subtitle: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",

    color: "#0C0E29",

    margin: "0px 0px 16px 0px",
  },

  dialog__contentContainer: {
    padding: "0px 16px !important",
    marginBottom: "16px",

    // remove scrollbar
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
  },

  dialog__content: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",

    margin: "0px !important",
  },

  errorContainer: {
    width: "100%",
    backgroundColor: "#FDF3F3",

    border: "1px solid #FBE5E5",
    borderRadius: "6px",

    marginBottom: "16px",
    padding: "12px",
  },

  errorMessage: {
    fontSize: "16px",
    color: "#D84949",
  },

  disclaimerContainer: {
    width: "100%",
    backgroundColor: "#F8F8F8",

    border: "1px solid #EDEDED",
    borderRadius: "6px",

    marginTop: "16px",
    padding: "12px",
  },

  disclaimerMessage: {
    fontSize: "16px",
    color: "#0C0E29",
  },

  priceDefinitionDescription: {
    fontSize: "16px",
    color: "#0C0E29",
    opacity: "0.8",

    margin: "0 0 1rem 0",
  },

  priceEntries: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  validFromDate: {
    margin: "0px 4px 4px 0",
    fontSize: "14px",
    color: "#666",
    textAlign: "right",
    fontWeight: "400",
  },

  priceEntry: {
    width: "100%",
    minHeight: "48px",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",

    padding: "8px",

    borderRadius: "8px",
    backgroundColor: "#F8F8F8",
    border: "1px solid #EDEDED",

    boxSizing: "border-box",

    transition: "all ease-in-out 0.2s",
  },

  priceInfo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "2px",
  },

  price: {
    margin: "0",
    fontSize: "16px",
    fontWeight: "500",
    color: "#0C0E29",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
  },

  form: {
    width: "100%",

    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },

  subscriptionLimitContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  inputContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  labelContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  inputError: {
    fontSize: "14px",
    color: "#D84949",

    margin: "0",
  },

  textField: {
    height: "40px",
  },

  radioButtonContainer: {
    width: "100%",
    display: "flex",
    gap: "8px",
  },

  radioButton: {
    width: "fit-content",
    height: "40px",

    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",

    padding: "8px",

    borderRadius: "4px",

    transition: "all ease-in-out 0.2s",

    "&:hover": {
      backgroundColor: "#EDEDED",
      cursor: "pointer",
    },
  },

  radio: {
    width: "20px",
    height: "20px",

    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    boxSizing: "border-box",

    borderRadius: "50%",
    border: "1px solid #0C0E29",
    backgroundColor: "transparent",
  },

  radioIcon: {
    color: "#F8F8F8",
  },

  radioButton__label: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",

    margin: "0",
  },

  saveButton: {
    height: "40px",
    padding: "0 16px",

    alignSelf: "end",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "white",
    backgroundColor: "var(--color-primary)",

    transition: "all 0.2s ease-in-out",

    userSelect: "none",

    "&:hover": {
      backgroundColor: "#a77d4f",
      cursor: "pointer",
    },
  },

  deleteButton: {
    height: "40px",
    padding: "0 16px",
    verticalAlign: "center",
    border: "1px solid #D84949",
    borderRadius: "4px",

    fontSize: "16px",
    color: "#D84949",
    backgroundColor: "transparent",

    transition: "all 0.2s ease-in-out",

    userSelect: "none",

    "&:hover": {
      backgroundColor: "#C73B3B",
      color: "white",
      border: "1px solid #C73B3B",
      cursor: "pointer",
    },
  },

  historyWithCorrectionLayout: {
    display: "flex",
    flexDirection: "row",
    gap: "16px",
    width: "100%",
  },

  priceEntrySelected: {
    backgroundColor: "#EEF0FF",
    borderColor: "#4654FB",
  },

  correctionPanel: {
    flex: "1 1 0",
    minWidth: "0",
    animation: "$slideInFromRight 0.3s ease-out",
  },

  correctionPanelContent: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "16px",
    borderRadius: "8px",
    backgroundColor: "#F8F8F8",
    border: "1px solid #EDEDED",
  },
}));
